<template>
  <div>
    <div class="table-toolbar mb-3">
      <div>
        <h5 class="mb-1">Employee Submission List</h5>
        <small class="text-muted">{{ description }}</small>
      </div>
      <div class="table-search">
        <b-form-input
          :value="search"
          placeholder="Search by name, NIK, position, department..."
          @input="$emit('update:search', $event)"
          @keyup.enter="$emit('search')"
        />
      </div>
    </div>

    <vue-good-table
      :columns="columns"
      :rows="indexedRows"
      :isLoading="isLoading"
      :search-options="{ enabled: false }"
      :pagination-options="{ enabled: false }"
      styleClass="vgt-table striped bordered condensed"
      @on-sort-change="$emit('sort-change', $event)"
    >
      <template #table-row="{ row, column }">
        <span v-if="column.field === 'total_learning'" class="submission-badge">
          {{ row.total_learning || 0 }} pengajuan
        </span>
        <span v-else-if="column.field === 'department'">
          {{ row.department || "-" }}
        </span>
        <span v-else-if="column.field === 'action'">
          <button
            class="btn btn-sm btn-outline-primary detail-btn"
            :disabled="!row.nik"
            @click="$emit('detail', row)"
          >
            <i class="fe fe-eye mr-1"></i>
            Detail
          </button>
        </span>
        <span v-else>{{ row[column.field] || "-" }}</span>
      </template>
    </vue-good-table>

    <div class="mt-4 pt-3 border-top">
      <div class="row align-items-center">
        <div class="col-md-4">
          <div class="d-flex align-items-center">
            <span class="text-muted small mr-2">Show</span>
            <select
              :value="pageSize"
              class="form-control form-control-sm page-size-select"
              @change="$emit('per-page-change', Number($event.target.value))"
            >
              <option v-for="option in pageSizeOptions" :key="option" :value="option">{{ option }}</option>
            </select>
            <span class="text-muted small ml-2">entries</span>
          </div>
        </div>
        <div class="col-md-4 text-center">
          <small class="text-muted">
            Showing {{ showingFrom }} to {{ showingTo }} of {{ total }} entries
          </small>
        </div>
        <div class="col-md-4">
          <nav>
            <ul class="pagination pagination-sm justify-content-end mb-0">
              <li class="page-item" :class="{ disabled: page <= 1 }">
                <button class="page-link" :disabled="page <= 1" @click="changePage(page - 1)">
                  <i class="fe fe-chevron-left"></i>
                </button>
              </li>
              <li
                v-for="pageNumber in visiblePages"
                :key="pageNumber"
                class="page-item"
                :class="{ active: page === pageNumber, disabled: pageNumber === '...' }"
              >
                <button v-if="pageNumber === '...'" class="page-link" disabled>...</button>
                <button v-else class="page-link" @click="changePage(pageNumber)">{{ pageNumber }}</button>
              </li>
              <li class="page-item" :class="{ disabled: page >= totalPages }">
                <button class="page-link" :disabled="page >= totalPages" @click="changePage(page + 1)">
                  <i class="fe fe-chevron-right"></i>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ApprovalEmployeeList",
  props: {
    rows: { type: Array, default: () => [] },
    search: { type: String, default: "" },
    isLoading: { type: Boolean, default: false },
    total: { type: Number, default: 0 },
    page: { type: Number, default: 1 },
    pageSize: { type: Number, default: 10 },
    showingFrom: { type: Number, default: 0 },
    showingTo: { type: Number, default: 0 },
    description: {
      type: String,
      default: "Diurutkan dari jumlah pengajuan yang menunggu approval terbanyak.",
    },
  },
  computed: {
    totalPages() {
      return Math.max(Math.ceil((this.total || 0) / (this.pageSize || 1)), 1);
    },
    visiblePages() {
      const pages = [];
      const totalPages = this.totalPages;
      const currentPage = Math.min(Math.max(this.page, 1), totalPages);

      if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i += 1) pages.push(i);
        return pages;
      }

      if (currentPage <= 4) {
        return [1, 2, 3, 4, 5, "...", totalPages];
      }

      if (currentPage >= totalPages - 3) {
        return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
      }

      return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
    },
    indexedRows() {
      const offset = (Math.max(this.page, 1) - 1) * this.pageSize;
      return this.rows.map((row, index) => ({
        ...row,
        no: offset + index + 1,
      }));
    },
  },
  data() {
    return {
      pageSizeOptions: [10, 25, 50, 100],
      columns: [
        { label: "No", field: "no", sortable: false, thClass: "table-header text-muted font-weight-bold" },
        { label: "Name", field: "name", sortable: true, thClass: "table-header text-muted font-weight-bold" },
        { label: "NIK", field: "nik", sortable: true, thClass: "table-header text-muted font-weight-bold" },
        { label: "Position", field: "position", sortable: true, thClass: "table-header text-muted font-weight-bold" },
        { label: "Department", field: "department", sortable: true, thClass: "table-header text-muted font-weight-bold" },
        { label: "Pengajuan", field: "total_learning", sortable: true, thClass: "table-header text-muted font-weight-bold" },
        { label: "Action", field: "action", sortable: false, thClass: "table-header text-muted font-weight-bold" },
      ],
    };
  },
  methods: {
    changePage(nextPage) {
      if (!nextPage || nextPage < 1 || nextPage > this.totalPages || nextPage === this.page) {
        return;
      }

      this.$emit("page-change", nextPage);
    },
  },
};
</script>

<style scoped>
.table-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.table-search {
  width: min(100%, 420px);
}

.submission-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: #fff1f2;
  color: #d92550;
  font-size: 12px;
  font-weight: 700;
  padding: 5px 10px;
  white-space: nowrap;
}

.detail-btn {
  border-color: #e11d2e;
  color: #e11d2e;
}

.page-size-select {
  width: 70px;
}

.pagination .page-link {
  color: #dc3545;
  border-color: #dee2e6;
}

.pagination .page-item.active .page-link {
  background-color: #dc3545;
  border-color: #dc3545;
  color: #fff;
}

.pagination .page-item.disabled .page-link {
  color: #6c757d;
  background-color: #fff;
  border-color: #dee2e6;
}

@media (max-width: 768px) {
  .table-toolbar {
    display: block;
  }

  .table-search {
    margin-top: 12px;
    width: 100%;
  }

  .pagination {
    justify-content: flex-start !important;
    margin-top: 12px;
  }

  .text-center {
    text-align: left !important;
    margin-top: 12px;
  }
}
</style>
